import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

// Pages
import LandingPage from "./pages/LandingPage";
import AuthPage from "./pages/AuthPage";
import Dashboard from "./pages/Dashboard";
import AllocationCalculator from "./pages/AllocationCalculator";
import ReserveRunway from "./pages/ReserveRunway";
import CashFlowProjection from "./pages/CashFlowProjection";
import ProfitLossCalculator from "./pages/ProfitLossCalculator";
import QuarterlyReview from "./pages/QuarterlyReview";
import TaxCalculator from "./pages/TaxCalculator";
import DeductionsLibrary from "./pages/DeductionsLibrary";
import MyDeductions from "./pages/MyDeductions";
import Settings from "./pages/Settings";
import BankAccounts from "./pages/BankAccounts";
import TaxPlans from "./pages/TaxPlans";
import ProfitFirstAccounts from "./pages/ProfitFirstAccounts";
import Subscription from "./pages/Subscription";
import Transactions from "./pages/Transactions";
import MileageTracker from "./pages/MileageTracker";
import HomeOfficeSetup from "./pages/HomeOfficeSetup";
import ReceiptVault from "./pages/ReceiptVault";
import DeductionFlags from "./pages/DeductionFlags";
import EstimatedTax from "./pages/EstimatedTax";
import Bookkeeping from "./pages/Bookkeeping";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import DataRetention from "./pages/DataRetention";
import SecurityPolicy from "./pages/SecurityPolicy";
import SmsTerms from "./pages/SmsTerms";
import Assistant from "./pages/Assistant";
import DemoView from "./pages/DemoView";
import WealthEngine from "./pages/WealthEngine";
import ChatBubble from "./components/ChatBubble";
import CookieBanner from "./components/CookieBanner";
import OnboardingTour from "./components/OnboardingTour";
import CodeProtection from "./components/CodeProtection";


const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

// Business Context
const BusinessContext = createContext(null);

export const useBusiness = () => {
  const context = useContext(BusinessContext);
  if (!context) {
    throw new Error("useBusiness must be used within a BusinessProvider");
  }
  return context;
};

// API instance
export const api = axios.create({
  baseURL: API,
  headers: {
    "Content-Type": "application/json",
  },
});

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);

  useEffect(() => {
    if (token) {
      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await api.get("/auth/me");
      setUser(response.data);
      fetchSubscription();
    } catch (error) {
      console.error("Failed to fetch user:", error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscription = async () => {
    try {
      const res = await api.get("/subscription/current");
      setSubscription(res.data);
    } catch (error) {
      console.error("Failed to fetch subscription:", error);
      setSubscription({ is_active: false, status: "none", tier: null });
    }
  };

  // Also allow checking subscription by email for owner bypass
  const checkOwnerBypass = (userData) => {
    const ownerEmails = ["info@tmariegreene.com"];
    return ownerEmails.includes(userData?.email);
  };

  const login = async (email, password) => {
    const response = await api.post("/auth/login", { email, password });
    // If MFA is required, return the response for the auth page to handle
    if (response.data.mfa_required) {
      return { mfa_required: true, temp_token: response.data.temp_token };
    }
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem("token", newToken);
    api.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const verifyMfa = async (tempToken, code) => {
    const response = await api.post("/auth/verify-mfa", { temp_token: tempToken, code });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem("token", newToken);
    api.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, fullName) => {
    const params = new URLSearchParams(window.location.search);
    const response = await api.post("/auth/register", {
      email,
      password,
      full_name: fullName,
      utm_source: params.get("utm_source") || null,
      utm_medium: params.get("utm_medium") || null,
      utm_campaign: params.get("utm_campaign") || null,
      signup_source: params.get("utm_source") ? "paid_ad" : "organic",
    });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem("token", newToken);
    api.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem("token");
    delete api.defaults.headers.common["Authorization"];
    setToken(null);
    setUser(null);
    setSubscription(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, verifyMfa, register, logout, subscription, fetchSubscription, checkOwnerBypass }}>
      {children}
    </AuthContext.Provider>
  );
};

// Business Provider
const BusinessProvider = ({ children }) => {
  const { token } = useAuth();
  const [businesses, setBusinesses] = useState([]);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchBusinesses = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const response = await api.get("/businesses");
      setBusinesses(response.data);
      if (response.data.length > 0 && !selectedBusiness) {
        const savedBusinessId = localStorage.getItem("selectedBusinessId");
        const business = response.data.find(b => b.id === savedBusinessId) || response.data[0];
        setSelectedBusiness(business);
      }
    } catch (error) {
      console.error("Failed to fetch businesses:", error);
    } finally {
      setLoading(false);
    }
  }, [token, selectedBusiness]);

  useEffect(() => {
    fetchBusinesses();
  }, [token]);

  const selectBusiness = (business) => {
    setSelectedBusiness(business);
    localStorage.setItem("selectedBusinessId", business.id);
  };

  const createBusiness = async (data) => {
    const response = await api.post("/businesses", data);
    setBusinesses([...businesses, response.data]);
    if (!selectedBusiness) {
      selectBusiness(response.data);
    }
    return response.data;
  };

  const updateBusiness = async (id, data) => {
    const response = await api.put(`/businesses/${id}`, data);
    setBusinesses(businesses.map(b => b.id === id ? response.data : b));
    if (selectedBusiness?.id === id) {
      setSelectedBusiness(response.data);
    }
    return response.data;
  };

  const deleteBusiness = async (id) => {
    await api.delete(`/businesses/${id}`);
    const remaining = businesses.filter(b => b.id !== id);
    setBusinesses(remaining);
    if (selectedBusiness?.id === id) {
      setSelectedBusiness(remaining[0] || null);
    }
  };

  return (
    <BusinessContext.Provider value={{
      businesses,
      selectedBusiness,
      loading,
      selectBusiness,
      createBusiness,
      updateBusiness,
      deleteBusiness,
      fetchBusinesses,
    }}>
      {children}
    </BusinessContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { token, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse flex flex-col items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-primary/20"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!token) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  return children;
};

// Main App Layout
const AppLayout = ({ children }) => {
  const { user, logout, token, subscription, checkOwnerBypass } = useAuth();
  const { businesses, selectedBusiness, selectBusiness } = useBusiness();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [expandedSections, setExpandedSections] = useState({ bookkeeping: true, financial: true, tax: true, deductions: true });
  const [showTour, setShowTour] = useState(false);

  // Redirect to subscription page if no active subscription (skip for owner)
  useEffect(() => {
    if (subscription && !subscription.is_active && !checkOwnerBypass(user) && location.pathname !== "/subscription" && !location.pathname.startsWith("/subscription")) {
      navigate("/subscription", { replace: true });
    }
  }, [subscription, location.pathname]);

  useEffect(() => {
    const tourDone = localStorage.getItem("opturna_tour_complete");
    if (!tourDone && businesses.length > 0) {
      const timer = setTimeout(() => setShowTour(true), 1500);
      return () => clearTimeout(timer);
    }
  }, [businesses]);

  const toggleSection = (key) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const navSections = [
    { key: "main", items: [
      { path: "/dashboard", label: "Dashboard", icon: "LayoutDashboard" },
    ]},
    { key: "bookkeeping", label: "Bookkeeping", items: [
      { path: "/bookkeeping", label: "Automated Bookkeeping", icon: "Receipt2" },
      { path: "/transactions", label: "Transactions", icon: "Receipt2" },
      { path: "/bank-accounts", label: "Bank Accounts", icon: "Bank" },
      { path: "/receipts", label: "Receipt Vault", icon: "Paperclip" },
    ]},
    { key: "financial", label: "Financial", items: [
      { path: "/smart-accounts", label: "Smart Accounts", icon: "Wallet" },
      { path: "/wealth", label: "Wealth Engine", icon: "TrendingUp" },
      { path: "/allocation", label: "Allocation Calculator", icon: "Calculator" },
      { path: "/reserve", label: "Reserve Runway", icon: "Shield" },
      { path: "/cashflow", label: "Cash Flow", icon: "TrendingUp" },
      { path: "/profit-loss", label: "Profit & Loss", icon: "PieChart" },
      { path: "/quarterly", label: "Quarterly Review", icon: "Calendar" },
    ]},
    { key: "tax", label: "Tax Planning", items: [
      { path: "/tax-calculator", label: "Tax Calculator", icon: "Receipt" },
      { path: "/tax-plans", label: "Tax Plans", icon: "Target" },
      { path: "/estimated-tax", label: "Estimated Tax", icon: "DollarSign" },
      { path: "/mileage", label: "Mileage Tracker", icon: "Car" },
      { path: "/home-office", label: "Home Office", icon: "Home" },
    ]},
    { key: "deductions", label: "Deductions", items: [
      { path: "/my-deductions", label: "My Deductions", icon: "Star" },
      { path: "/deduction-flags", label: "Opportunities", icon: "Flag" },
      { path: "/deductions", label: "Deductions Library", icon: "BookOpen" },
    ]},
    { key: "ai", items: [
      { path: "/assistant", label: "AI Assistant", icon: "MessageCircle" },
    ]},
  ];

  const isGatedRoute = (path) => path === "/wealth";
  const hasWealthTier = (subscription?.is_active && subscription?.tier === "wealth_builder") || checkOwnerBypass(user);
  const isActive = (path) => location.pathname === path;
  const sectionHasActive = (items) => items.some(i => isActive(i.path));

  const IconComponent = ({ name, className }) => {
    const icons = {
      LayoutDashboard: (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <rect x="3" y="3" width="7" height="7" rx="1" />
          <rect x="14" y="3" width="7" height="7" rx="1" />
          <rect x="3" y="14" width="7" height="7" rx="1" />
          <rect x="14" y="14" width="7" height="7" rx="1" />
        </svg>
      ),
      Calculator: (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <rect x="4" y="2" width="16" height="20" rx="2" />
          <line x1="8" y1="6" x2="16" y2="6" />
          <line x1="8" y1="10" x2="8" y2="10.01" />
          <line x1="12" y1="10" x2="12" y2="10.01" />
          <line x1="16" y1="10" x2="16" y2="10.01" />
          <line x1="8" y1="14" x2="8" y2="14.01" />
          <line x1="12" y1="14" x2="12" y2="14.01" />
          <line x1="16" y1="14" x2="16" y2="14.01" />
        </svg>
      ),
      Shield: (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        </svg>
      ),
      TrendingUp: (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
          <polyline points="17 6 23 6 23 12" />
        </svg>
      ),
      PieChart: (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M21.21 15.89A10 10 0 1 1 8 2.83" />
          <path d="M22 12A10 10 0 0 0 12 2v10z" />
        </svg>
      ),
      Calendar: (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <rect x="3" y="4" width="18" height="18" rx="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
      ),
      Receipt: (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1-2-1z" />
          <line x1="8" y1="6" x2="16" y2="6" />
          <line x1="8" y1="10" x2="16" y2="10" />
          <line x1="8" y1="14" x2="12" y2="14" />
        </svg>
      ),
      BookOpen: (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
          <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
        </svg>
      ),
      Target: (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <circle cx="12" cy="12" r="10" />
          <circle cx="12" cy="12" r="6" />
          <circle cx="12" cy="12" r="2" />
        </svg>
      ),
      Wallet: (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M21 12V7H5a2 2 0 010-4h14v4" />
          <path d="M3 5v14a2 2 0 002 2h16v-5" />
          <path d="M18 12a2 2 0 100 4 2 2 0 000-4z" />
        </svg>
      ),
      Bank: (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M3 21h18" />
          <path d="M3 10h18" />
          <path d="M5 6l7-3 7 3" />
          <path d="M4 10v11" />
          <path d="M20 10v11" />
          <path d="M8 14v3" />
          <path d="M12 14v3" />
          <path d="M16 14v3" />
        </svg>
      ),
      Receipt2: (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M9 14l6-6" />
          <circle cx="9.5" cy="9.5" r="0.5" fill="currentColor" />
          <circle cx="14.5" cy="14.5" r="0.5" fill="currentColor" />
          <path d="M5 21V5a2 2 0 012-2h10a2 2 0 012 2v16l-3-2-2 2-2-2-2 2-2-2-3 2z" />
        </svg>
      ),
      Flag: (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
          <line x1="4" y1="22" x2="4" y2="15" />
        </svg>
      ),
      Car: (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M14 16H9m10 0h3v-3.15a1 1 0 00-.84-.99L16 11l-2.7-3.6a1 1 0 00-.8-.4H5.24a2 2 0 00-1.8 1.1l-.8 1.63A6 6 0 002 12.42V16h2" />
          <circle cx="6.5" cy="16.5" r="2.5" />
          <circle cx="16.5" cy="16.5" r="2.5" />
        </svg>
      ),
      Home: (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
          <polyline points="9,22 9,12 15,12 15,22" />
        </svg>
      ),
      Paperclip: (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
        </svg>
      ),
      DollarSign: (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <line x1="12" y1="1" x2="12" y2="23" />
          <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
        </svg>
      ),
      Star: (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        </svg>
      ),
      Settings: (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
        </svg>
      ),
      LogOut: (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
          <polyline points="16 17 21 12 16 7" />
          <line x1="21" y1="12" x2="9" y2="12" />
        </svg>
      ),
      Menu: (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <line x1="3" y1="12" x2="21" y2="12" />
          <line x1="3" y1="6" x2="21" y2="6" />
          <line x1="3" y1="18" x2="21" y2="18" />
        </svg>
      ),
      ChevronDown: (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      ),
      MessageCircle: (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z" />
        </svg>
      ),
    };
    return icons[name] || null;
  };

  return (
    <div className="min-h-screen bg-[#f5f7fa] flex">
      <OnboardingTour run={showTour} onComplete={() => setShowTour(false)} />
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'w-60' : 'w-16'} bg-white border-r border-[#e4e8ee] flex flex-col transition-all duration-200 fixed h-full z-30`}>
        {/* Logo */}
        <div className="h-16 flex items-center px-4 border-b border-[#e4e8ee]">
          <div className="flex items-center gap-2.5">
            {sidebarOpen ? (
              <img src="/logo_compact.png" alt="Opturna Tax AI" className="h-8 rounded" data-testid="sidebar-logo" />
            ) : (
              <div className="w-8 h-8 rounded-lg bg-[#1e3a5f] flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold text-sm">O</span>
              </div>
            )}
          </div>
        </div>

        {/* Business Selector */}
        {sidebarOpen && businesses.length > 0 && (
          <div className="px-3 py-3 border-b border-[#e4e8ee]">
            <select
              value={selectedBusiness?.id || ""}
              onChange={(e) => {
                const business = businesses.find(b => b.id === e.target.value);
                if (business) selectBusiness(business);
              }}
              className="w-full px-2.5 py-1.5 rounded-md border border-[#e4e8ee] bg-white text-xs font-medium text-[#1e3a5f] focus:outline-none focus:ring-1 focus:ring-[#1e3a5f]/20"
              data-testid="business-selector"
            >
              {businesses.map(business => (
                <option key={business.id} value={business.id}>{business.name}</option>
              ))}
            </select>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 px-2 py-3 overflow-y-auto">
          {navSections.map((section) => (
            <div key={section.key} className="mb-1">
              {/* Section header with dropdown toggle */}
              {section.label && sidebarOpen && (
                <button
                  onClick={() => toggleSection(section.key)}
                  className="sidebar-section-header"
                  data-testid={`nav-section-${section.key}`}
                >
                  <span>{section.label}</span>
                  <IconComponent
                    name="ChevronDown"
                    className={`w-3.5 h-3.5 transition-transform duration-200 ${expandedSections[section.key] ? '' : '-rotate-90'}`}
                  />
                </button>
              )}

              {/* Section items */}
              {(!section.label || expandedSections[section.key] || !sidebarOpen) && (
                <ul className={section.label && sidebarOpen ? 'animate-expand' : ''}>
                  {section.items.map(item => (
                    <li key={item.path}>
                      <button
                        onClick={() => navigate(item.path)}
                        className={`sidebar-nav-item ${isActive(item.path) ? 'active' : 'text-[#5a6577]'}`}
                        data-testid={`nav-${item.path.slice(1)}`}
                        title={!sidebarOpen ? item.label : undefined}
                      >
                        <IconComponent name={item.icon} className={`w-[18px] h-[18px] flex-shrink-0 ${isActive(item.path) ? 'text-[#1e3a5f]' : ''}`} />
                        {sidebarOpen && (
                          <span className="text-[13px] flex items-center gap-1.5">
                            {item.label}
                            {isGatedRoute(item.path) && !hasWealthTier && (
                              <svg className="w-3 h-3 text-amber-500" viewBox="0 0 24 24" fill="currentColor"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" fill="none" stroke="currentColor" strokeWidth="2" /></svg>
                            )}
                          </span>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </nav>

        {/* Bottom Actions */}
        <div className="px-2 py-3 border-t border-[#e4e8ee] space-y-0.5">
          <button
            onClick={() => navigate("/settings")}
            className={`sidebar-nav-item ${isActive('/settings') ? 'active' : 'text-[#5a6577]'}`}
            data-testid="nav-settings"
          >
            <IconComponent name="Settings" className="w-[18px] h-[18px]" />
            {sidebarOpen && <span className="text-[13px]">Settings</span>}
          </button>
          <button
            onClick={() => { logout(); navigate("/"); }}
            className="sidebar-nav-item text-[#5a6577] hover:!bg-red-50 hover:!text-red-600"
            data-testid="logout-button"
          >
            <IconComponent name="LogOut" className="w-[18px] h-[18px]" />
            {sidebarOpen && <span className="text-[13px]">Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`flex-1 ${sidebarOpen ? 'ml-60' : 'ml-16'} transition-all duration-200`}>
        {/* Top Bar */}
        <header className="h-14 bg-white border-b border-[#e4e8ee] flex items-center justify-between px-6 sticky top-0 z-20">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-1.5 rounded-md hover:bg-[#f5f7fa] transition-colors"
            data-testid="toggle-sidebar"
          >
            <IconComponent name="Menu" className="w-5 h-5 text-[#5a6577]" />
          </button>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-sm font-medium text-[#1e3a5f]">{user?.full_name}</p>
              <p className="text-[11px] text-[#8b95a5]">{user?.email}</p>
            </div>
            <div className="w-8 h-8 rounded-full bg-[#1e3a5f] flex items-center justify-center">
              <span className="text-white font-semibold text-xs">
                {user?.full_name?.charAt(0)?.toUpperCase()}
              </span>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <div className="p-6">
          {children}
        </div>
      </main>
      <ChatBubble token={token} businessId={selectedBusiness?.id} />
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/data-retention" element={<DataRetention />} />
          <Route path="/security" element={<SecurityPolicy />} />
          <Route path="/sms-terms" element={<SmsTerms />} />
          <Route path="/demo" element={<DemoView />} />
          <Route path="/admin" element={<AdminLogin />} />
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <BusinessProvider>
                  <AppLayout>
                    <Routes>
                      <Route path="/dashboard" element={<Dashboard />} />
                      <Route path="/smart-accounts" element={<ProfitFirstAccounts />} />
                      <Route path="/allocation" element={<AllocationCalculator />} />
                      <Route path="/reserve" element={<ReserveRunway />} />
                      <Route path="/cashflow" element={<CashFlowProjection />} />
                      <Route path="/profit-loss" element={<ProfitLossCalculator />} />
                      <Route path="/quarterly" element={<QuarterlyReview />} />
                      <Route path="/tax-calculator" element={<TaxCalculator />} />
                      <Route path="/tax-plans" element={<TaxPlans />} />
                      <Route path="/my-deductions" element={<MyDeductions />} />
                      <Route path="/deductions" element={<DeductionsLibrary />} />
                      <Route path="/bank-accounts" element={<BankAccounts />} />
                      <Route path="/transactions" element={<Transactions />} />
                      <Route path="/mileage" element={<MileageTracker />} />
                      <Route path="/home-office" element={<HomeOfficeSetup />} />
                      <Route path="/receipts" element={<ReceiptVault />} />
                      <Route path="/deduction-flags" element={<DeductionFlags />} />
                      <Route path="/estimated-tax" element={<EstimatedTax />} />
                      <Route path="/bookkeeping" element={<Bookkeeping />} />
                      <Route path="/assistant" element={<Assistant />} />
                      <Route path="/wealth" element={<WealthEngine />} />
                      <Route path="/subscription" element={<Subscription />} />
                      <Route path="/subscription/success" element={<Subscription />} />
                      <Route path="/subscription/cancel" element={<Subscription />} />
                      <Route path="/settings" element={<Settings />} />
                      <Route path="*" element={<Navigate to="/dashboard" replace />} />
                    </Routes>
                  </AppLayout>
                </BusinessProvider>
              </ProtectedRoute>
            }
          />
        </Routes>
        <CookieBanner />
        <CodeProtection />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
